// Port "Mirrors" — Teil A: Real-Time-Cube-Reflexionen. Aus Jans Renderer.
//
// HERKUNFT
//
// pro-editor/src/storyforger/Renderer/RendererWebGL.ts — updateRealtimeReflections
// + setObjectEnvMap (~125 Zeilen). Faithful `this.` -> `host.`.
//
// WAS ES TUT
//
// Für jedes object3d-Layer mit content.realtimeReflections rendert es die Szene
// pro Frame in eine CubeCamera an der Objektposition und setzt deren Textur als
// envMap auf die Meshes. So spiegelt das Objekt ANDERE Szenen-Objekte — die
// HDRI-only scene.environment kann das nicht. Opt-in, teuer (6-Flächen-Render
// pro reflektierendem Objekt).
//
// TEIL B (NICHT hier): planare Spiegel (THREE.Reflector, updateMirrors). Deren
// Flag verlangt `primitive === 'plane'` — das allgemeine Primitive-Subsystem,
// das wir noch nicht portiert haben. Kommt mit dem Primitive-Port.
//
// ABHÄNGIGKEIT: baut auf dem HDRI-Port auf (zeigt für die Cube-Pässe transient
// den Skydome, damit die HDRI in der Reflexion erscheint). ensureSkyDome kommt
// aus environment-hdri.
//
// ADDITIV-SICHERHEIT: ohne gesetztes realtimeReflections-Flag ist reflectiveLayers
// leer -> updateRealtimeReflections returned früh. Bestand unverändert.
//
// VERIFIKATION: visuell (Devview /__dev__/realtime-reflections).
import THREE from "../utils/THREE";
import { ensureSkyDome, type EnvironmentHost } from "./environment-hdri";
import { istMobilesGeraet } from "../utils/geraet";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TODOAny = any;

/**
 * Der Teil des Renderers, den die Cube-Reflexionen anfassen. Erweitert
 * EnvironmentHost (für ensureSkyDome + die HDRI-Dome-Felder).
 */
export interface ReflectionHost extends EnvironmentHost {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layerMeshes: { [id: string]: any };
    reflectiveLayers: { [id: string]: boolean };
    reflectionRigs: {
        [id: string]: {
            camera: THREE.CubeCamera;
            rt: THREE.WebGLCubeRenderTarget;
            /** letzter Cube-Render (ms) — Drossel, s. unten */
            lastRender?: number;
            /** Objekt war unsichtbar und wurde uebersprungen → beim naechsten
             *  Sichtbarwerden sofort neu fuellen (Jan 11.09.2026). */
            veraltet?: boolean;
        };
    };
}

// Assign (or clear) an env map on every mesh of an object. needsUpdate only
// fires when the reference actually changes, so there's no per-frame recompile.
export function setObjectEnvMap(obj: TODOAny, tex: THREE.Texture | null) {
    obj.traverse((child: TODOAny) => {
        if (!child.isMesh) return;
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach((m: TODOAny) => {
            if (m && "envMap" in m && m.envMap !== tex) {
                m.envMap = tex;
                m.needsUpdate = true;
            }
        });
    });
}

// Opt-in real-time reflections. For each object flagged with
// realtimeReflections, render the scene into a CubeCamera from the object's
// position and use that as its env map, so it reflects other scene objects
// (the HDRI-only scene.environment can't). Run once per frame before the main
// render. Heavy (a 6-face scene render per reflective object), hence opt-in.
export function updateRealtimeReflections(host: ReflectionHost) {
    if (!host.webGLRenderer || !host.webGlScene) return;
    const ids = Object.keys(host.reflectiveLayers);

    // tear down rigs that are no longer needed (flag off or object gone)
    for (const id of Object.keys(host.reflectionRigs)) {
        if (!host.reflectiveLayers[id] || !host.layerMeshes[id]) {
            const obj = host.layerMeshes[id];
            if (obj) setObjectEnvMap(obj, null);
            host.reflectionRigs[id].rt.dispose();
            delete host.reflectionRigs[id];
        }
    }

    const active = ids.filter((id) => host.reflectiveLayers[id] && host.layerMeshes[id]);
    if (active.length === 0) return;

    // make sure world matrices are current so each rig sits on its object
    host.webGlScene.updateMatrixWorld(true);

    // temporarily show the HDRI dome at neutral exposure/zoom so the environment
    // appears in the reflection even when "show as background" is off, and so
    // the reflected HDRI isn't skewed by the background display settings
    let restoreSky: { visible: boolean; zoom: number; exp: number; map: TODOAny } | undefined;
    if (host.environmentEquirect) {
        ensureSkyDome(host);
        const m = host.skyDomeMaterial!;
        restoreSky = {
            visible: host.skyDome!.visible,
            zoom: m.uniforms.uZoom.value,
            exp: m.uniforms.uExposure.value,
            map: m.uniforms.map.value,
        };
        m.uniforms.map.value = host.environmentEquirect;
        m.uniforms.uZoom.value = 1;
        m.uniforms.uExposure.value = 1;
        host.skyDome!.visible = true;
    }

    // Transmissive (glass) materials render BLACK inside a CubeCamera pass —
    // three.js only runs its transmission render-target pass for the main view,
    // so glass samples an empty buffer and goes black in the reflection. Zero
    // out transmission for the duration of the cube renders (uniform only, no
    // needsUpdate / recompile) so glass shows up as a plausible glossy surface
    // instead of a black blob, then restore the original values afterwards.
    const transmissionStash: Array<{ mat: TODOAny; value: number }> = [];
    host.webGlScene.traverse((o: TODOAny) => {
        if (!o.isMesh) return;
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m: TODOAny) => {
            if (m && m.isMeshPhysicalMaterial && m.transmission > 0) {
                transmissionStash.push({ mat: m, value: m.transmission });
                m.transmission = 0;
            }
        });
    });

    // Hide planar mirrors for the cube passes: a Reflector re-renders the whole
    // scene in its onBeforeRender, so leaving it visible would do that inside
    // every one of the 6 cube faces (per reflective object) — far too costly.
    // Mirrors simply won't show up inside cube-map reflections. (Teil B; heute
    // gibt es keine mirrorRigs, der Zweig ist ein No-op.)
    const hiddenMirrors: TODOAny[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mirrorRigs = (host as any).mirrorRigs as { [id: string]: { reflector: TODOAny } } | undefined;
    if (mirrorRigs) {
        for (const mid of Object.keys(mirrorRigs)) {
            const r = mirrorRigs[mid].reflector;
            if (r && r.visible) {
                r.visible = false;
                hiddenMirrors.push(r);
            }
        }
    }

    // DROSSEL (Jans Power-Test 11.09.2026, in seinem sichtbaren Chrome gemessen):
    // drei Chrom-Objekte = 18 Cube-Faces je Bild → 31 ms/Bild (~32 fps) statt
    // 11 ms ohne. Mobil galt schon 66 ms (#476); der Standbild-Spiegel (#480)
    // wurde zurueckgebaut, weil er nicht gut aussah — darum hier KEIN
    // Einfrieren, sondern: hoechstens EIN Rig pro Bild, das am laengsten
    // wartende zuerst, und je Rig mindestens 33 ms Abstand am Desktop. Bei
    // einem Objekt bleiben 30 Updates/s, bei dreien 20/s. Die Erstfuellung
    // (lastRender undefiniert) und ein Objekt, das unsichtbar uebersprungen
    // wurde (veraltet), kommen immer sofort dran. Der planare Reflector-
    // Spiegel (planar-mirrors) bleibt unangetastet: ein Spiegel muss ein
    // Spiegel sein (Jan).
    const jetzt = typeof performance !== "undefined" ? performance.now() : Date.now();
    const drossel = istMobilesGeraet() ? 66 : 33;
    const RIGS_PRO_BILD = 1;
    const faellig: string[] = [];
    for (const id of active) {
        const obj = host.layerMeshes[id] as TODOAny;
        let rig = host.reflectionRigs[id];
        if (!rig) {
            // Mobil 128 statt 256: sechs Faces je verspiegeltem Objekt — auf
            // Handy-Groesse reicht die halbe Kantenlaenge (Viertel-Speicher).
            const rt = new THREE.WebGLCubeRenderTarget(istMobilesGeraet() ? 128 : 256, {
                type: THREE.HalfFloatType,
                generateMipmaps: true,
                minFilter: THREE.LinearMipmapLinearFilter,
            });
            const camera = new THREE.CubeCamera(1, 200000, rt);
            rig = { camera, rt };
            host.reflectionRigs[id] = rig;
        }
        setObjectEnvMap(obj, rig.rt.texture);
        // BEIFANG (Jan 11.09.2026): Objekte ausserhalb ihres Zeitfensters
        // (renderLayer setzt visible=false) brauchen keine sechs Faces je
        // Bild — nur merken, dass ihr Spiegelbild veraltet ist.
        if (!obj.visible || !obj.parent) {
            if (rig.lastRender !== undefined) rig.veraltet = true;
            continue;
        }
        const sofort = rig.lastRender === undefined || rig.veraltet;
        if (!sofort && jetzt - rig.lastRender! < drossel) continue;
        faellig.push(id);
    }
    // Das am laengsten wartende zuerst (Erstfuellung/veraltet ganz vorn).
    faellig.sort((a, b) => {
        const ra = host.reflectionRigs[a];
        const rb = host.reflectionRigs[b];
        const ta = ra.lastRender === undefined || ra.veraltet ? -Infinity : ra.lastRender;
        const tb = rb.lastRender === undefined || rb.veraltet ? -Infinity : rb.lastRender;
        return ta - tb;
    });
    let budget = RIGS_PRO_BILD;
    for (const id of faellig) {
        const rig = host.reflectionRigs[id];
        const sofort = rig.lastRender === undefined || rig.veraltet;
        if (!sofort && budget <= 0) break;
        if (!sofort) budget--;
        const obj = host.layerMeshes[id] as TODOAny;
        rig.lastRender = jetzt;
        rig.veraltet = false;
        obj.getWorldPosition(rig.camera.position);
        const prevVisible = obj.visible;
        obj.visible = false; // don't let the object reflect itself
        rig.camera.update(host.webGLRenderer, host.webGlScene);
        obj.visible = prevVisible;
        setObjectEnvMap(obj, rig.rt.texture);
    }

    // restore the glass materials' transmission for the main render
    transmissionStash.forEach(({ mat, value }) => {
        mat.transmission = value;
    });
    // show the planar mirrors again for the main render
    hiddenMirrors.forEach((r) => {
        r.visible = true;
    });

    if (restoreSky && host.skyDome && host.skyDomeMaterial) {
        const m = host.skyDomeMaterial;
        m.uniforms.map.value = restoreSky.map;
        m.uniforms.uZoom.value = restoreSky.zoom;
        m.uniforms.uExposure.value = restoreSky.exp;
        host.skyDome.visible = restoreSky.visible;
    }
}
